import { EducationImportType } from '@/api/education'
import { useEducationImportMutation, useEducationUploadMutation } from '@/api/educationQueryOptions'
import { FileType } from '@/api/file'
import FileUpload from '@/components/file/FileUpload'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import SaveIcon from '@mui/icons-material/Save'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { GridColDef, GridLogicOperator } from '@mui/x-data-grid-premium'
import { DataGridPremium } from '@mui/x-data-grid-premium/DataGridPremium'
import { createFileRoute } from '@tanstack/react-router'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import React from 'react'

export const Route = createFileRoute('/_auth/educationImport')({
    component: EducationImportComponent,
})

function EducationImportComponent() {

    const educationUploadMutation = useEducationUploadMutation(
        (data) => {
            setImportedEducations(data)
        },
        () => {
            enqueueSnackbar(t('error.educationImported'), { variant: 'error' });
        }
    )

    const educationImportMutation = useEducationImportMutation(
        () => {
            setImportedEducations([])
            enqueueSnackbar(t('action.educationImported'), { variant: 'success' });
        },
        () => {
            enqueueSnackbar(t('error.educationImported'), { variant: 'error' });
        }
    )

    const [importedEducations, setImportedEducations] = React.useState<EducationImportType[]>([])

    const errorCount = importedEducations.filter((education) => education.importError).length

    const onSave = () => {
        if (importedEducations.length > 0) {
            educationImportMutation.mutate(importedEducations)
        }
    }

    const columns: GridColDef[] = [];
    columns.push(
        {
            field: 'importScoutId',
            headerName: 'Scout-ID',
            valueGetter: (_value, row) => `${row.importScoutId}`,
            minWidth: 120,
        },
        {
            field: 'importName',
            headerName: 'Import-Name',
            valueGetter: (_value, row) => `${row.importName}`,
            minWidth: 120,
        },
        {
            field: 'member.name',
            headerName: 'Name',
            valueGetter: (_value, row) => `${row.member?.firstname} ${row.member?.lastname}`,
        },
        {
            field: 'importEducationStepCode',
            headerName: 'Import-Ausbildungsschritt',
            minWidth: 150,
        },
        {
            field: 'educationStep.code',
            headerName: 'Ausbildungsschritt',
            valueGetter: (_value, row) => `${row.educationStep.code}`,
            minWidth: 150,
        },
        {
            field: 'importCompleteDate',
            headerName: 'Abschlussdatum',
            valueGetter: (_value, row) => row.importCompleteDate && new Date(row.importCompleteDate),
            type: 'date',
            minWidth: 120,
        },
        {
            field: 'importAddition',
            headerName: 'Zusatz',
        },
        {
            field: 'importComment',
            headerName: 'Kommentar',
        },
        {
            field: 'importWarning',
            headerName: 'Hinweise',
        },
        {
            field: 'importError',
            headerName: 'Fehler',
        },
    )

    const onFileUpload = (file: FileType) => {
        educationUploadMutation.mutate(file)
    }

    return (
        <>
            <FileUpload accept='.xlsx' onUpload={onFileUpload} />
            <Box sx={{
                height: { xs: `calc(100% - 50px)` }
            }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>

                    <DataGridPremium
                        columns={columns}
                        rows={importedEducations}
                        getRowId={(row) => { return row.uuid || '' }}
                        pagination={false}
                        slots={{
                            toolbar: (props) => {
                                return <OverviewGridToolbar {...props} scope="educationImport" >
                                    {(importedEducations.length > 0 && errorCount == 0) &&
                                        <Button variant="text" startIcon={<SaveIcon />} onClick={onSave}>Speichern</Button>
                                    }
                                </OverviewGridToolbar>
                            }
                        }}
                        slotProps={{
                            filterPanel: {
                                logicOperators: [GridLogicOperator.And],
                                sx: { maxWidth: '100vw' }
                            },
                            toolbar: {
                                csvOptions: {
                                    disableToolbarButton: true
                                },
                                printOptions: {
                                    disableToolbarButton: true
                                },
                                excelOptions: {
                                    disableToolbarButton: true
                                }
                            },
                        }}
                        density='compact'
                        hideFooter={true}
                        disableDensitySelector
                        disableAggregation
                        disableColumnPinning
                        disableColumnReorder
                        disableRowGrouping
                        showToolbar
                    />
                </div>
            </Box>
        </>
    );
}
